import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable Country Dropdown Component
 * Features:
 * - Fetches countries from backend (includes dynamic/user-added countries)
 * - Schengen sub-dropdown
 * - Custom country input with validation
 * - Automatically adds valid custom countries to system
 */
const CountryDropdown = ({
  value,
  onChange,
  required = false,
  label = "Select Country",
  placeholder = "Please Select Country",
  error = null,
  disabled = false,
  className = "",
  showLabel = true,
  size = "default" // "default" | "small"
}) => {
  const [countries, setCountries] = useState([]);
  const [schengenCountries, setSchengenCountries] = useState([]);
  const [showSchengen, setShowSchengen] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customCountry, setCustomCountry] = useState('');
  const [schengenSelection, setSchengenSelection] = useState('');
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [loading, setLoading] = useState(true);

  // Fetch countries on mount
  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/api/countries`);
      setCountries(response.data || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
      // Fallback to default list
      setCountries([
        { name: "USA", is_schengen: false, is_primary: true },
        { name: "UK", is_schengen: false, is_primary: true },
        { name: "Canada", is_schengen: false, is_primary: true },
        { name: "Australia", is_schengen: false, is_primary: true },
        { name: "New Zealand", is_schengen: false, is_primary: true },
        { name: "Germany", is_schengen: true, is_primary: true },
        { name: "Schengen (Europe)", is_schengen: true, is_primary: true },
        { name: "UAE", is_schengen: false, is_primary: true },
        { name: "Singapore", is_schengen: false, is_primary: true },
        { name: "Other", is_schengen: false, is_primary: true }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchengenCountries = async () => {
    try {
      const response = await axios.get(`${API}/api/schengen-countries`);
      const countryNames = response.data.map(c => c.name).filter(Boolean);
      setSchengenCountries(countryNames);
    } catch (error) {
      console.error('Error fetching Schengen countries:', error);
      setSchengenCountries(['Germany', 'France', 'Spain', 'Italy', 'Netherlands']);
    }
  };

  const handleMainChange = async (selectedValue) => {
    setValidationError('');
    setCustomCountry('');
    setSchengenSelection('');
    
    if (selectedValue === 'Schengen (Europe)' || selectedValue.toLowerCase() === 'schengen') {
      setShowSchengen(true);
      setShowCustomInput(false);
      await fetchSchengenCountries();
      // Don't call onChange yet - wait for Schengen country selection
    } else if (selectedValue === 'Other') {
      setShowSchengen(false);
      setShowCustomInput(true);
      // Don't call onChange yet - wait for custom country input
    } else {
      setShowSchengen(false);
      setShowCustomInput(false);
      onChange(selectedValue);
    }
  };

  const handleSchengenChange = (schengenValue) => {
    setSchengenSelection(schengenValue);
    if (schengenValue) {
      onChange(schengenValue);
    }
  };

  const handleCustomCountryChange = (e) => {
    setCustomCountry(e.target.value);
    setValidationError('');
  };

  const validateAndAddCustomCountry = useCallback(async () => {
    if (!customCountry.trim()) {
      setValidationError('Please enter a country name');
      return;
    }

    setValidating(true);
    setValidationError('');

    try {
      const response = await axios.post(`${API}/api/public/validate-and-add-country`, {
        name: customCountry.trim()
      });

      if (response.data.valid) {
        const normalizedName = response.data.normalized_name;
        onChange(normalizedName);
        setShowCustomInput(false);
        setCustomCountry('');
        
        // Refresh countries list to include the new one
        if (response.data.added_to_list) {
          fetchCountries();
        }
      } else {
        setValidationError(response.data.message || 'Invalid country name');
      }
    } catch (error) {
      console.error('Error validating country:', error);
      setValidationError('Failed to validate country. Please try again.');
    } finally {
      setValidating(false);
    }
  }, [customCountry, onChange]);

  const handleCustomKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      validateAndAddCustomCountry();
    }
  };

  const inputStyle = size === 'small' ? {
    height: '36px',
    fontSize: '13px'
  } : {
    height: '44px',
    fontSize: '14px'
  };

  const baseSelectStyle = {
    width: '100%',
    padding: '0 12px',
    borderRadius: '8px',
    border: `1px solid ${error || validationError ? '#ef4444' : '#e2e8f0'}`,
    outline: 'none',
    transition: 'border-color 0.2s',
    backgroundColor: disabled ? '#f1f5f9' : 'white',
    cursor: disabled ? 'not-allowed' : 'pointer',
    ...inputStyle
  };

  const baseInputStyle = {
    width: '100%',
    padding: '0 12px',
    borderRadius: '8px',
    border: `1px solid ${validationError ? '#ef4444' : '#e2e8f0'}`,
    outline: 'none',
    transition: 'border-color 0.2s',
    ...inputStyle
  };

  // Determine current display value for main dropdown
  const getMainDropdownValue = () => {
    if (showSchengen) return 'Schengen (Europe)';
    if (showCustomInput) return 'Other';
    
    // Check if value matches a country in our list
    const matchedCountry = countries.find(c => c.name === value);
    if (matchedCountry) return value;
    
    // Check if it's a Schengen country
    if (schengenCountries.includes(value)) return 'Schengen (Europe)';
    
    return value || '';
  };

  if (loading) {
    return (
      <div className={className}>
        {showLabel && <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>{label}</label>}
        <select style={baseSelectStyle} disabled>
          <option>Loading countries...</option>
        </select>
      </div>
    );
  }

  return (
    <div className={className}>
      {showLabel && (
        <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
          {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
        </label>
      )}
      
      {/* Main Country Dropdown */}
      <select
        value={getMainDropdownValue()}
        onChange={(e) => handleMainChange(e.target.value)}
        style={baseSelectStyle}
        disabled={disabled}
        data-testid="country-dropdown-main"
      >
        <option value="">{placeholder}</option>
        {countries.map((country, index) => (
          <option key={index} value={country.name}>
            {country.name}
          </option>
        ))}
      </select>

      {/* Schengen Sub-dropdown */}
      {showSchengen && (
        <div style={{ marginTop: '8px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: '#6b7280' }}>
            Select Schengen Country {required && <span style={{ color: '#ef4444' }}>*</span>}
          </label>
          <select
            value={schengenSelection}
            onChange={(e) => handleSchengenChange(e.target.value)}
            style={baseSelectStyle}
            data-testid="country-dropdown-schengen"
          >
            <option value="">Please Select Schengen Country</option>
            {schengenCountries.map((country, index) => (
              <option key={index} value={country}>{country}</option>
            ))}
          </select>
        </div>
      )}

      {/* Custom Country Input */}
      {showCustomInput && (
        <div style={{ marginTop: '8px' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: '#6b7280' }}>
            Enter Country Name {required && <span style={{ color: '#ef4444' }}>*</span>}
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              value={customCountry}
              onChange={handleCustomCountryChange}
              onKeyDown={handleCustomKeyDown}
              placeholder="Enter country name"
              style={{ ...baseInputStyle, flex: 1 }}
              data-testid="country-dropdown-custom"
            />
            <button
              type="button"
              onClick={validateAndAddCustomCountry}
              disabled={validating || !customCountry.trim()}
              style={{
                padding: '0 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: validating ? '#9ca3af' : '#3b82f6',
                color: 'white',
                fontWeight: '500',
                cursor: validating || !customCountry.trim() ? 'not-allowed' : 'pointer',
                ...inputStyle
              }}
              data-testid="country-dropdown-validate"
            >
              {validating ? '...' : 'Add'}
            </button>
          </div>
          {validationError && (
            <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{validationError}</p>
          )}
          <p style={{ color: '#9ca3af', fontSize: '11px', marginTop: '4px' }}>
            Enter a valid country name. It will be validated and added to the list.
          </p>
        </div>
      )}

      {/* Error message */}
      {error && !validationError && (
        <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>{error}</p>
      )}
    </div>
  );
};

export default CountryDropdown;
